import { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { useAppStore } from '@/stores/appStore';
import { supabase, getSession } from '@/lib/supabase';
import { Analytics } from '@vercel/analytics/react';

// Layouts
import MainLayout from '@/layouts/MainLayout';
import AuthLayout from '@/layouts/AuthLayout';

// Public Pages
import PublicLayout from '@/features/public/PublicLayout';
import Home from '@/features/public/Home';
import HowItWorks from '@/features/public/HowItWorks';
import About from '@/features/public/About';
import Pricing from '@/features/public/Pricing';
import Contact from '@/features/public/Contact';

// Auth Pages
import Login from '@/features/auth/Login';
import Register from '@/features/auth/Register';
import OrgSignup from '@/features/auth/OrgSignup';
import OrgLogin from '@/features/auth/OrgLogin';
import MemberSignup from '@/features/auth/MemberSignup';
import MemberLogin from '@/features/auth/MemberLogin';

// Main Pages
import Dashboard from '@/features/dashboard/Dashboard';
import OrganizationSelect from '@/features/organizations/OrganizationSelect';
import OrganizationCreate from '@/features/organizations/OrganizationCreate';

// Feature Pages
import Members from '@/features/members/Members';
import MemberDetail from '@/features/members/MemberDetail';
import Attendance from '@/features/attendance/Attendance';
import MeetingAttendance from '@/features/attendance/MeetingAttendance';
import Dues from '@/features/dues/Dues';
import DuesCollection from '@/features/dues/DuesCollection';
import Donations from '@/features/donations/Donations';
import DonationEventDetail from '@/features/donations/DonationEventDetail';
import Finances from '@/features/finances/Finances';
import Settings from '@/features/settings/Settings';
import MemberPortal from '@/features/member-portal/MemberPortal';
import Profile from '@/features/profile/Profile';

// Payment Pages
import Subscription from '@/features/payment/Subscription';
import Checkout from '@/features/payment/Checkout';
import BankTransfer from '@/features/payment/BankTransfer';
import PendingBankTransfer from '@/features/payment/PendingBankTransfer';

// Components
import ProtectedRoute from '@/components/ProtectedRoute';
import RoleRoute from '@/components/RoleRoute';
import OfflineIndicator from '@/components/OfflineIndicator';
import SyncIndicator from '@/components/SyncIndicator';
import ScrollToTop from '@/components/ScrollToTop';

function App() {
  const { 
    setUser, 
    setSession, 
    setIsAuthenticated, 
    setIsLoading
  } = useAppStore();

  // Tracks whether the boot-time auth check has finished.
  // onAuthStateChange fires SIGNED_IN during initAuth on page load —
  // we skip the duplicate profile fetch until initAuth is done.
  const initAuthDone = useRef(false);

  // Initialize auth state
  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      setIsLoading(true);

      try {
        // getSession() already has its own internal timeout (4 s), so we
        // await it directly — no outer race needed. A second, outer timeout
        // only causes the session to silently drop on slow-but-working
        // connections, producing a login-page flash.
        const sessionResult = await getSession();

        if (cancelled) return;

        const { data: { session } } = sessionResult;

        if (session) {
          setSession(session);
          setIsAuthenticated(true);

          // Single authoritative profile fetch on boot
          const { data: userData } = await supabase
            .from('users')
            .select('*')
            .eq('id', session.user.id)
            .single();
          
          if (!cancelled && userData) {
            setUser(userData);
          }
        } else {
          setSession(null);
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        if (!cancelled) console.error('Auth initialization error:', error);
      } finally {
        if (!cancelled) {
          // Mark boot-time check as complete so the auth listener below
          // knows subsequent SIGNED_IN events are genuine new sign-ins.
          initAuthDone.current = true;
          setIsLoading(false);
        }
      }
    };

    initAuth();

    // Listen for auth state changes that happen AFTER the initial boot check.
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          setSession(session);
          setIsAuthenticated(true);

          // Skip the profile fetch during the boot-time SIGNED_IN event —
          // initAuth() already handles it. Only fetch for genuine new sign-ins
          // (e.g. user just logged in on the login page).
          if (initAuthDone.current) {
            const { data: userData } = await supabase
              .from('users')
              .select('*')
              .eq('id', session.user?.id)
              .single();
            
            if (userData) {
              setUser(userData);
            }
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setSession(null);
          setIsAuthenticated(false);
        } else if (event === 'TOKEN_REFRESHED' && session) {
          setSession(session);
          setIsAuthenticated(true);
        }
      }
    );

    return () => {
      cancelled = true;
      authListener.subscription.unsubscribe();
    };
  }, [setUser, setSession, setIsAuthenticated, setIsLoading]);

  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen bg-background">
        <Routes>
          {/* Public Routes */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
          </Route>

          {/* New Auth Flows */}
          <Route path="/org-signup" element={<OrgSignup />} />
          <Route path="/org-login" element={<OrgLogin />} />
          <Route path="/member-signup" element={<MemberSignup />} />
          <Route path="/member-login" element={<MemberLogin />} />

          {/* Legacy Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
          </Route>

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>  
            <Route element={<MainLayout />}>
              {/* Organization Selection */}
              <Route path="/organizations" element={<OrganizationSelect />} />
              <Route path="/organizations/create" element={<OrganizationCreate />} />
              
              {/* Dashboard */}
              <Route path="/dashboard" element={<Dashboard />} />
              
              {/* Member Portal (accessible to all) */}
              <Route path="/my-record" element={<MemberPortal />} />
              <Route path="/profile" element={<Profile />} />
              
              {/* Admin/Treasurer/Secretary Routes */}
              <Route element={<RoleRoute allowedRoles={['admin', 'treasurer', 'secretary']} />}>
                <Route path="/members" element={<Members />} />
                <Route path="/members/:id" element={<MemberDetail />} />
                <Route path="/attendance" element={<Attendance />} />
                <Route path="/attendance/:meetingId" element={<MeetingAttendance />} />
                <Route path="/dues" element={<Dues />} />
                <Route path="/dues/collect" element={<DuesCollection />} />
                <Route path="/donations" element={<Donations />} />
                <Route path="/donations/:eventId" element={<DonationEventDetail />} />
              </Route>
              
              {/* Admin/Treasurer Only Routes */}
              <Route element={<RoleRoute allowedRoles={['admin', 'treasurer']} />}>
                <Route path="/finances" element={<Finances />} />
              </Route>
              
              {/* Subscription & Payment Routes (Admin Only) */}
              <Route element={<RoleRoute allowedRoles={['admin']} />}>
                <Route path="/subscription" element={<Subscription />} />
                <Route path="/payment/checkout" element={<Checkout />} />
                <Route path="/payment/bank-transfer" element={<BankTransfer />} />
                <Route path="/payment/pending" element={<PendingBankTransfer />} />
              </Route>
              
              {/* Admin Only Routes */}
              <Route element={<RoleRoute allowedRoles={['admin']} />}>
                <Route path="/settings" element={<Settings />} />
              </Route>
            </Route>
          </Route>

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
        <Toaster position="top-right" richColors />
        <OfflineIndicator />
        <SyncIndicator />
        <Analytics />
      </div>
    </Router>
  );
}

export default App;
